import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_PUBLIC_KEY = 'T7N_mqSWB7oDCvy_2';
const EMAILJS_SERVICE_ID = 'service_jt86wux';
const EMAILJS_TEMPLATE_ID = 'template_qpn0xfs';

const ContactForm = ({ variant = 'default' }) => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const schema = z.object({
    firstName: z.string().min(1, t('form.validation.firstName')),
    email: z.string().email(t('form.validation.email')),
    phone: z.string().min(1, t('form.validation.phone')),
    city: z.string().min(1, t('form.validation.city')),
    message: z.string().min(1, t('form.validation.message')),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare template parameters for EmailJS
      const templateParams = {
        from_name: data.firstName,
        from_email: data.email,
        phone: data.phone,
        city: data.city,
        message: data.message,
        to_email: 'eurofortaleza.mtk@gmail.com',
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('EmailJS Response:', response);
      
      if (response.status === 200) {
        setSubmitStatus('success');
        reset();
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full bg-white border border-gray-200 p-4 text-navy-deep focus:border-gold-main focus:ring-1 focus:ring-gold-main outline-none transition-colors placeholder:text-gray-400";
  const labelClasses = "block text-sm font-semibold text-navy-deep mb-2 uppercase tracking-wide";
  const errorClasses = "text-red-500 text-sm mt-1";

  if (submitStatus === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
        data-testid="form-success"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="font-alatsi text-2xl text-navy-deep mb-3">
          {t('form.success')}
        </h3>
        <p className="text-gray-600 mb-6">
          {t('form.successSubtitle')}
        </p>
        <button
          onClick={() => setSubmitStatus(null)}
          className="bg-navy-deep text-white px-8 py-3 uppercase tracking-widest text-sm font-semibold hover:bg-gold-main transition-colors"
          data-testid="form-send-another"
        >
          {language === 'en' ? 'Send Another Message' : 'Enviar Outra Mensagem'}
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className={labelClasses}>
            {t('form.firstName')} *
          </label>
          <input
            id="firstName"
            type="text"
            {...register('firstName')}
            placeholder={t('form.placeholders.firstName')}
            className={inputClasses}
            data-testid="form-firstname"
            aria-invalid={errors.firstName ? 'true' : 'false'}
          />
          {errors.firstName && (
            <p className={errorClasses} role="alert">{errors.firstName.message}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={labelClasses}>
            {t('form.email')} *
          </label>
          <input
            id="email"
            type="email"
            {...register('email')}
            placeholder={t('form.placeholders.email')}
            className={inputClasses}
            data-testid="form-email"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {errors.email && (
            <p className={errorClasses} role="alert">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className={labelClasses}>
            {t('form.phone')} *
          </label>
          <input
            id="phone"
            type="tel"
            {...register('phone')}
            placeholder={t('form.placeholders.phone')}
            className={inputClasses}
            data-testid="form-phone"
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
          {errors.phone && (
            <p className={errorClasses} role="alert">{errors.phone.message}</p>
          )}
        </div>

        {/* City */}
        <div>
          <label htmlFor="city" className={labelClasses}>
            {t('form.city')} *
          </label>
          <input
            id="city"
            type="text"
            {...register('city')}
            placeholder={t('form.placeholders.city')}
            className={inputClasses}
            data-testid="form-city"
            aria-invalid={errors.city ? 'true' : 'false'}
          />
          {errors.city && (
            <p className={errorClasses} role="alert">{errors.city.message}</p>
          )}
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className={labelClasses}>
          {t('form.message')} *
        </label>
        <textarea
          id="message"
          rows={5}
          {...register('message')}
          placeholder={t('form.placeholders.message')}
          className={`${inputClasses} resize-none`}
          data-testid="form-message"
          aria-invalid={errors.message ? 'true' : 'false'}
        />
        {errors.message && (
          <p className={errorClasses} role="alert">{errors.message.message}</p>
        )}
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 text-red-600 bg-red-50 p-4"
            data-testid="form-error"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{t('form.error')}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto bg-navy-deep text-white hover:bg-gold-main transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-sm font-semibold flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="form-submit"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('form.sending')}</span>
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            <span>{t('form.submit')}</span>
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;
